import React, { useEffect } from 'react';
import type { DesignNodeData, EngineeringBillingMode } from '../../types';
import {
  resolveApmRate,
  resolveJoineryRates,
  type DesignRateInputs,
} from '../../utils/proposalDesignBreakdown';
import {
  joineryPeopleForLine,
  matchJoineryPerson,
  withAssignedLeadDesigner,
  type JoineryLineKey,
} from '../../utils/proposalJoineryPeople';
import { useAssignedProjectDesigner, useJoineryPeople } from '../../utils/useJoineryPeople';
import { useCRM } from '../../utils/CRMContext';
import {
  structureCompareRowProps,
  type StructureLineCompareMap,
} from '../../utils/structureLineCompare';
import './HardwareListTable.css';
import './ProposalBreakdownGroups.css';

const GroupTitle: React.FC<{ label: string; extra?: React.ReactNode }> = ({ label, extra }) => (
  <div className="hardware-group-title">
    <span>{label}</span>
    {extra}
  </div>
);

interface DesignBreakdownGroupsProps {
  design: DesignNodeData;
  rates: DesignRateInputs;
  hoursDisabled?: boolean;
  ratesDisabled?: boolean;
  formatCurrency: (value: number) => string;
  onDesignChange: (patch: Partial<DesignNodeData>) => void;
  onRateChange?: (field: keyof DesignRateInputs, value: number) => void;
  onBlur?: () => void;
  compareFields?: StructureLineCompareMap;
}

const DesignBreakdownGroups: React.FC<DesignBreakdownGroupsProps> = ({
  design,
  rates,
  hoursDisabled,
  ratesDisabled,
  formatCurrency,
  onDesignChange,
  onRateChange,
  onBlur,
  compareFields,
}) => {
  const { crmUsers } = useCRM();
  const { staff, designers } = useJoineryPeople();
  const assignedDesigner = useAssignedProjectDesigner();
  const lump = design.engineeringMode === 'lumpSum';
  const joineryRates = resolveJoineryRates(design, rates);
  const apmRate = resolveApmRate(joineryRates);
  const readonly = hoursDisabled && ratesDisabled ? ' hardware-table--readonly' : '';

  useEffect(() => {
    if (design.leadDesignerUserId || !assignedDesigner.uid) return;
    const designer = designers.find((person) => person.userId === assignedDesigner.uid);
    const next = withAssignedLeadDesigner(
      design,
      assignedDesigner,
      designer?.hourlyRate != null ? { designerHourlyRate: designer.hourlyRate } : null,
      rates.leadDesignerRate || 120,
    );
    if (next.leadDesignerUserId !== design.leadDesignerUserId) {
      onDesignChange({
        leadDesignerUserId: next.leadDesignerUserId,
        leadDesignerRate: next.leadDesignerRate,
      });
    }
  }, [assignedDesigner.uid, design.leadDesignerUserId, designers, rates.leadDesignerRate]);

  const joineryRateInput = (
    field: 'pmRate' | 'apmRate' | 'leadDesignerRate' | 'supportDesignerRate',
    value: number,
  ) =>
    !ratesDisabled ? (
      <input
        type="number"
        min="0"
        step="0.01"
        value={value || ''}
        onChange={(e) => onDesignChange({ [field]: parseFloat(e.target.value) || 0 })}
        onBlur={onBlur}
      />
    ) : (
      <span>{formatCurrency(value)}</span>
    );

  const rateInput = (
    field: keyof DesignRateInputs,
    value: number,
  ) =>
    onRateChange && !ratesDisabled ? (
      <input
        type="number"
        min="0"
        step="0.01"
        value={value || ''}
        onChange={(e) => onRateChange(field, parseFloat(e.target.value) || 0)}
        onBlur={onBlur}
      />
    ) : (
      <span>{formatCurrency(value)}</span>
    );

  const hourInput = (value: number, field: keyof DesignNodeData) => (
    <input
      type="number"
      min="0"
      step="0.5"
      value={value || ''}
      disabled={hoursDisabled}
      onChange={(e) => onDesignChange({ [field]: parseFloat(e.target.value) || 0 })}
      onBlur={onBlur}
    />
  );

  const joineryRows: Array<{
    key: JoineryLineKey;
    name: string;
    hours: number;
    hourField: keyof DesignNodeData;
    rate: number;
    rateField: 'pmRate' | 'apmRate' | 'leadDesignerRate' | 'supportDesignerRate';
    userId?: string;
    userField: 'pmUserId' | 'apmUserId' | 'leadDesignerUserId' | 'supportDesignerUserId';
    fallbackRate: number;
  }> = [
    {
      key: 'pm',
      name: 'PM',
      hours: design.pmHours,
      hourField: 'pmHours',
      rate: joineryRates.pmRate,
      rateField: 'pmRate',
      userId: design.pmUserId,
      userField: 'pmUserId',
      fallbackRate: rates.pmRate,
    },
    {
      key: 'apm',
      name: 'APM',
      hours: design.apmHours ?? 0,
      hourField: 'apmHours',
      rate: apmRate,
      rateField: 'apmRate',
      userId: design.apmUserId,
      userField: 'apmUserId',
      fallbackRate: resolveApmRate(rates),
    },
    {
      key: 'lead',
      name: 'Lead designer',
      hours: design.leadDesignerHours,
      hourField: 'leadDesignerHours',
      rate: joineryRates.leadDesignerRate,
      rateField: 'leadDesignerRate',
      userId: design.leadDesignerUserId,
      userField: 'leadDesignerUserId',
      fallbackRate: rates.leadDesignerRate || 120,
    },
    {
      key: 'support',
      name: 'Support designer',
      hours: design.supportDesignerHours,
      hourField: 'supportDesignerHours',
      rate: joineryRates.supportDesignerRate,
      rateField: 'supportDesignerRate',
      userId: design.supportDesignerUserId,
      userField: 'supportDesignerUserId',
      fallbackRate: rates.supportDesignerRate,
    },
  ];

  return (
    <div className="timber-list-container hardware-list-wrap">
      <div className="hardware-group" aria-label="Joinery">
        <GroupTitle label="Joinery" />
        <table className={`hardware-table hardware-table--qty-cost${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Name</th>
              <th className="hardware-col-num">Hours</th>
              <th className="hardware-col-num">$/hr</th>
              <th className="hardware-col-num">Total</th>
            </tr>
          </thead>
          <tbody>
            {joineryRows.map((row) => {
              let people = joineryPeopleForLine(row.key, staff, designers);
              if (row.key === 'lead' && assignedDesigner.uid && !people.some((person) => person.userId === assignedDesigner.uid)) {
                const assigned = crmUsers.find((user) => user.uid === assignedDesigner.uid);
                people = [
                  {
                    id: assignedDesigner.uid,
                    name: assignedDesigner.name || assigned?.displayName || assignedDesigner.uid,
                    title: assigned?.title,
                    userId: assignedDesigner.uid,
                    hourlyRate: designers.find((person) => person.userId === assignedDesigner.uid)?.hourlyRate ?? null,
                  },
                  ...people,
                ];
              } else if (row.userId && !people.some((person) => person.userId === row.userId)) {
                const linked = crmUsers.find((user) => user.uid === row.userId);
                people = [
                  {
                    id: row.userId,
                    name: linked?.displayName || row.userId,
                    title: linked?.title,
                    userId: row.userId,
                    hourlyRate: designers.find((person) => person.userId === row.userId)?.hourlyRate
                      ?? staff.find((person) => person.userId === row.userId)?.hourlyRate
                      ?? null,
                  },
                  ...people,
                ];
              }
              const selected = matchJoineryPerson(people, row.userId);
              const selectedTitle = (selected?.title || '').trim();
              const selectedName = (selected?.name || '').trim();
              const showTitle =
                !!selectedTitle && selectedTitle.toLowerCase() !== selectedName.toLowerCase();
              const compareKey =
                row.key === 'pm' ? 'design.pm'
                : row.key === 'apm' ? 'design.apm'
                : row.key === 'lead' ? 'design.lead'
                : 'design.support';
              const compare = structureCompareRowProps(compareFields, compareKey);
              return (
                <tr key={row.key} className={compare.className} title={compare.title}>
                  <td className="hardware-col-name">
                    <div className="joinery-person-line">
                      <span className="joinery-role-badge">{row.name}</span>
                      <select
                        value={selected?.userId || ''}
                        disabled={hoursDisabled || ratesDisabled}
                        aria-label={`${row.name} person`}
                        onChange={(e) => {
                          const nextId = e.target.value;
                          if (!nextId) {
                            onDesignChange({ [row.userField]: '', [row.rateField]: row.fallbackRate });
                            return;
                          }
                          const person = matchJoineryPerson(people, nextId);
                          onDesignChange({
                            [row.userField]: nextId,
                            [row.rateField]: person?.hourlyRate != null
                              ? person.hourlyRate
                              : (row.rate || row.fallbackRate),
                          });
                        }}
                        onBlur={onBlur}
                      >
                        <option value="">Link person…</option>
                        {people.map((person) => (
                          <option key={person.id} value={person.userId} title={person.title || undefined}>
                            {person.name}
                          </option>
                        ))}
                      </select>
                      {showTitle && <span className="joinery-title-badge">{selectedTitle}</span>}
                    </div>
                  </td>
                  <td className="hardware-col-num">{hourInput(row.hours, row.hourField)}</td>
                  <td className="hardware-col-num">{joineryRateInput(row.rateField, row.rate)}</td>
                  <td className="hardware-col-num">
                    <span>{formatCurrency(row.hours * row.rate)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="hardware-group" aria-label="Engineering">
        <GroupTitle
          label="Engineering"
          extra={(
            <div className="breakdown-mode-toggle" role="group" aria-label="Engineering billing">
              {(['hourly', 'lumpSum'] as EngineeringBillingMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={(!lump && mode === 'hourly') || (lump && mode === 'lumpSum') ? 'active' : ''}
                  disabled={hoursDisabled}
                  onClick={() => onDesignChange({ engineeringMode: mode })}
                >
                  {mode === 'hourly' ? 'Hourly' : 'Lump sum'}
                </button>
              ))}
            </div>
          )}
        />
        <table className={`hardware-table hardware-table--qty-cost${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Name</th>
              <th className="hardware-col-num">{lump ? '' : 'Hours'}</th>
              <th className="hardware-col-num">{lump ? '' : '$/hr'}</th>
              <th className="hardware-col-num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr {...structureCompareRowProps(compareFields, 'design.engineering')}>
              <td className="hardware-col-name">
                <span className="hardware-ref-text">Engineering</span>
              </td>
              <td className="hardware-col-num">
                {lump ? null : hourInput(design.engineeringHours, 'engineeringHours')}
              </td>
              <td className="hardware-col-num">
                {lump ? null : rateInput('engineeringRate', rates.engineeringRate)}
              </td>
              <td className="hardware-col-num">
                {lump ? (
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={design.engineeringLumpSum || ''}
                    disabled={hoursDisabled}
                    onChange={(e) => onDesignChange({ engineeringLumpSum: parseFloat(e.target.value) || 0 })}
                    onBlur={onBlur}
                  />
                ) : (
                  <span>{formatCurrency(design.engineeringHours * rates.engineeringRate)}</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DesignBreakdownGroups;
